/**
 * Sign in with Google.
 *
 * Renders nothing at all unless the deployment has a client id — a button that fails after the
 * user has already left for a consent screen is worse than no button, and every environment that
 * has not configured Google should look as though the feature does not exist.
 *
 * The Google script is loaded on demand rather than in `index.html`: a third-party script on
 * every page load, including the ninety per cent of them that are already-signed-in app screens,
 * is a tracker and a slowdown for no benefit.
 *
 * **The credential it returns is not proof of anything here.** It is posted to the API, which
 * verifies it against Google before issuing a session. Nothing in this file is trusted.
 */

import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";

const SCRIPT_SRC = "https://accounts.google.com/gsi/client";

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (options: Record<string, unknown>) => void;
          renderButton: (parent: HTMLElement, options: Record<string, unknown>) => void;
        };
      };
    };
  }
}

function loadScript(): Promise<void> {
  if (document.querySelector(`script[src="${SCRIPT_SRC}"]`)) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = SCRIPT_SRC;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Google could not be reached."));
    document.head.appendChild(script);
  });
}

export function GoogleButton({
  onSignedIn,
  onNeedsSignUp,
}: {
  onSignedIn: () => void | Promise<void>;
  /**
   * Google confirmed somebody we have never seen.
   *
   * `signupToken` is the proof — opaque, single use, fifteen minutes. The address and name are
   * for display only; the server reads the real ones off its own record when the form is sent.
   */
  onNeedsSignUp?: (details: { signupToken: string; email: string; name: string }) => void;
}) {
  const holder = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);

  /**
   * The callbacks, held in a ref.
   *
   * They are inline arrow functions at every call site, so they are a new identity on every
   * render. Depending on them made the effect re-run constantly — and each run called Google's
   * `renderButton` again into a container that already had a button in it, which is why the
   * button appeared sometimes and not others. A ref keeps them current without being a dependency.
   */
  const handlers = useRef({ onSignedIn, onNeedsSignUp });
  handlers.current = { onSignedIn, onNeedsSignUp };

  const { data: config } = useQuery({
    queryKey: ["google-config"],
    queryFn: () => api.get<{ enabled: boolean; clientId: string | null }>("/auth/google/config"),
    staleTime: Infinity,
  });

  const clientId = config?.enabled ? config.clientId : null;

  useEffect(() => {
    if (!clientId) return;
    let cancelled = false;

    loadScript()
      .then(() => {
        const node = holder.current;
        if (cancelled || !node || !window.google) return;

        window.google.accounts.id.initialize({
          client_id: clientId,
          callback: async (response: { credential?: string }) => {
            if (!response.credential) return;
            try {
              const result = await api.post<{
                ok: boolean;
                needsSignUp?: boolean;
                signupToken?: string;
                email?: string;
                name?: string;
              }>("/auth/google", { credential: response.credential });

              if (result.ok) await handlers.current.onSignedIn();
              else if (result.needsSignUp && result.signupToken) {
                handlers.current.onNeedsSignUp?.({
                  signupToken: result.signupToken,
                  email: result.email ?? "",
                  name: result.name ?? "",
                });
              }
            } catch (problem) {
              setError(problem instanceof Error ? problem.message : "That did not work.");
            }
          },
        });

        // Empty first. Rendering into a node that already holds a button is what produced two
        // buttons in development and, sometimes, none at all.
        node.replaceChildren();
        window.google.accounts.id.renderButton(node, {
          theme: "outline",
          size: "large",
          width: 320,
          text: "continue_with",
          // Google's default puts the logo hard left and centres the label in what is left,
          // which opens a gap across a wide button. Centring the pair keeps them together.
          logo_alignment: "center",
        });
      })
      .catch(() => setError("Google could not be reached."));

    return () => {
      cancelled = true;
    };
    // Only the client id matters. See the note on `handlers` above.
  }, [clientId]);

  if (!config?.enabled) return null;

  return (
    <div className="mt-5">
      <div className="flex items-center gap-3">
        <span className="h-px flex-1 bg-ink-200" />
        <span className="text-xs uppercase tracking-wide text-ink-400">or</span>
        <span className="h-px flex-1 bg-ink-200" />
      </div>

      <div ref={holder} className="mt-4 flex justify-center" />
      {error ? <p className="mt-2 text-center text-sm text-danger-600">{error}</p> : null}
    </div>
  );
}
